import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SvgIcon,
  Tooltip,
} from '@mui/material';
import { RemixiconComponentType } from '@remixicon/react';
import { MouseEventHandler, ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import {
  getIconColorForStatus,
  getIconElementForStatus,
  StatusIcon,
} from '../utils/icon';
import iotsIconSvg from '../assets/icons/iots-icon.svg';

export interface SideActionItem {
  id: string;
  label: string;
  statusIcon?: StatusIcon;
  disabled?: boolean | string;
  url?: string;
  isExactUrl?: boolean;
  onClick?: MouseEventHandler;
}

export interface SideActionsListProps {
  className?: string;
  actions: SideActionItem[];
}

const renderStatusIconElement = (
  iconStatus: StatusIcon,
  isActive: boolean,
): ReactNode => {
  const iconElement: RemixiconComponentType | null =
    getIconElementForStatus(iconStatus);

  if (!iconElement) {
    return null;
  }
  return (
    <ListItemIcon className="min-w-7">
      <SvgIcon
        component={iconElement}
        className={isActive ? 'fill-white' : undefined}
        color={isActive ? 'inherit' : getIconColorForStatus(iconStatus)}
        fontSize="small"
      />
    </ListItemIcon>
  );
};

const SideActionsList = ({ actions, className }: SideActionsListProps) => {
  const { pathname } = useLocation();
  return (
    <List
      className={[
        'p-0 flex flex-wrap md:flex-col gap-2 md:gap-4',
        className ?? '',
      ].join(' ')}
    >
      {actions.map(item => {
        let isActive = false;
        if (item.url) {
          isActive =
            pathname === item.url ||
            (!item.isExactUrl && pathname.startsWith(item.url + '/'));
        }
        const buttonContent = (
          <ListItemButton
            className={`overflow-hidden rounded-[8px] py-0 px-2 md:py-[10px] md:px-4 md:text-[15px]`}
            disabled={Boolean(item.disabled) || (!item.url && !item.onClick)}
            onClick={item.disabled || item.url ? undefined : item.onClick}
          >
            {item.statusIcon &&
              renderStatusIconElement(item.statusIcon, isActive)}
            <ListItemText className="capitalize" primary={item.label} />
          </ListItemButton>
        );
        return (
          <Tooltip
            key={item.id}
            title={typeof item.disabled === 'string' ? item.disabled : ''}
            disableHoverListener={!item.disabled}
          >
            <ListItem
              className={[
                'w-auto md:w-full rounded-md md:rounded-lg p-0',
                isActive && 'bg-iots-brand text-white',
                !isActive && 'bg-iots-lightgrey',
                !item.disabled &&
                  (isActive
                    ? 'hover:bg-iots-emerald-700'
                    : 'hover:bg-iots-lightgreyhover'),
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {item.url ? (
                <Link
                  to={item.url}
                  onClick={event => {
                    if (item.disabled) {
                      event.preventDefault();
                    } else {
                      item.onClick?.(event);
                    }
                  }}
                  className="w-full"
                  {...(isActive ? { 'aria-current': 'page' } : {})}
                >
                  {buttonContent}
                </Link>
              ) : (
                buttonContent
              )}
            </ListItem>
          </Tooltip>
        );
      })}
      <div className="flex-auto hidden md:block relative overflow-hidden pointer-events-none">
        <img className="opacity-50 h-[194px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]" src={iotsIconSvg} />
      </div>
    </List>
  );
};

export default SideActionsList;
